import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, Users, Wrench, Home } from 'lucide-react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../lib/api';
import 'leaflet/dist/leaflet.css';

interface Address {
  street?: string;
  number?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface Owner {
  id: string;
  person?: {
    name?: string;
    document?: string;
    email?: string;
    phone?: string;
  };
}

interface Lease {
  id: string;
  propertyId?: string;
  code: string;
  status: string;
  startDate: string;
  endDate?: string;
  rentAmount: string | number;
  tenant?: {
    name: string;
  };
}

interface Property {
  id: string;
  code: string;
  type: string;
  status: string;
  title: string;
  description?: string;
  bedrooms?: number;
  bathrooms?: number;
  salePrice?: number | string;
  rentPrice?: number | string;
  address?: Address;
  latitude?: number | string;
  longitude?: number | string;
  owners?: Owner[];
  leases?: Lease[];
}

interface StatusMeta {
  label: string;
  color: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const DEFAULT_CENTER: [number, number] = [-25.4284, -49.2733];

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const formatCurrency = (value?: number | string) => {
  const numeric = toNumber(value);
  if (numeric === undefined) return '-';
  return numeric.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const buildAddressLabel = (property: Property) => {
  const parts = [
    property.address?.street,
    property.address?.number,
    property.address?.neighborhood,
    property.address?.city,
    property.address?.state,
  ].filter(Boolean);
  return parts.join(', ');
};

const buildMarkerIcon = (color: string) =>
  L.divIcon({
    className: '',
    html: `
      <div style="
        width: 18px;
        height: 18px;
        border-radius: 999px;
        background: ${color};
        border: 2px solid #fff;
        box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.18);
      "></div>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
  });

export default function MapDashboard() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [geoMsg, setGeoMsg] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'AVAILABLE' | 'RENTED' | 'MAINTENANCE'>('ALL');

  const loadProperties = async () => {
    try {
      const [propertiesRes, leasesRes] = await Promise.all([
        api.get('/properties?limit=500'),
        api.get('/leases?limit=500'),
      ]);

      const propertiesData = (propertiesRes.data?.data || []) as Property[];
      const leasesData = (leasesRes.data?.data || []) as Lease[];

      // Usa as coordenadas já salvas (geocodificação é feita no backend).
      const withLeases = propertiesData.map((property) => ({
        ...property,
        latitude: toNumber(property.latitude),
        longitude: toNumber(property.longitude),
        leases: leasesData.filter((lease) => lease.propertyId === property.id),
      }));

      setProperties(withLeases);
    } catch (error) {
      console.error('Erro ao carregar imoveis do mapa:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
  }, []);

  // Geocodifica no servidor (sem CORS/429) os imóveis sem coordenadas e recarrega.
  const handleGeocode = async () => {
    if (geocoding) return;
    setGeocoding(true);
    setGeoMsg('Localizando imóveis sem mapa... pode levar até ~1 min.');
    try {
      const { data } = await api.post('/properties/geocode-missing', { limit: 25 });
      setGeoMsg(
        `Localizados ${data.geocodificados}. Faltam ~${data.restantes}` +
          (data.restantes > 0 ? ' — clique novamente para continuar.' : '.'),
      );
      await loadProperties();
    } catch {
      setGeoMsg('Falha ao localizar imóveis.');
    } finally {
      setGeocoding(false);
    }
  };

  const getStatusMeta = (property: Property): StatusMeta & { activeLease?: Lease } => {
    const activeLease = property.leases?.find((lease) => lease.status === 'ACTIVE');

    if (property.status === 'MAINTENANCE') {
      return {
        label: 'Em manutencao/reforma',
        color: '#eab308',
        Icon: Wrench,
        activeLease,
      };
    }

    if (property.status === 'RENTED' || activeLease) {
      return {
        label: 'Alugado',
        color: '#ef4444',
        Icon: Users,
        activeLease,
      };
    }

    return {
      label: 'Disponivel',
      color: '#22c55e',
      Icon: Home,
      activeLease,
    };
  };

  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      if (filterStatus === 'ALL') return true;
      const statusMeta = getStatusMeta(property);
      if (filterStatus === 'AVAILABLE') return statusMeta.label === 'Disponivel';
      if (filterStatus === 'RENTED') return statusMeta.label === 'Alugado';
      return statusMeta.label === 'Em manutencao/reforma';
    });
  }, [properties, filterStatus]);

  const propertiesWithCoordinates = useMemo(() => {
    return filteredProperties.filter((property) => {
      const lat = toNumber(property.latitude);
      const lng = toNumber(property.longitude);
      return lat !== undefined && lng !== undefined;
    });
  }, [filteredProperties]);

  const mapCenter = useMemo<[number, number]>(() => {
    const first = propertiesWithCoordinates[0];
    if (!first) return DEFAULT_CENTER;

    const lat = toNumber(first.latitude);
    const lng = toNumber(first.longitude);
    if (lat === undefined || lng === undefined) return DEFAULT_CENTER;

    return [lat, lng];
  }, [propertiesWithCoordinates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando mapa de imoveis...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Mapa de Imoveis</h1>
          <p className="text-gray-600 mt-1">
            {filteredProperties.length} imoveis carregados
            {' • '}
            {propertiesWithCoordinates.length} com posicao no mapa
          </p>
        </div>

        <div className="flex items-end gap-3">
          <button
            onClick={handleGeocode}
            disabled={geocoding}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
            title="Buscar a posição no mapa dos imóveis que ainda não têm coordenadas"
          >
            {geocoding ? 'Localizando...' : 'Localizar no mapa'}
          </button>
          <div className="w-full max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status no Mapa</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'ALL' | 'AVAILABLE' | 'RENTED' | 'MAINTENANCE')}
              className="input"
            >
              <option value="ALL">Todos</option>
              <option value="AVAILABLE">Disponiveis</option>
              <option value="RENTED">Alugados</option>
              <option value="MAINTENANCE">Manutencao/Reforma</option>
            </select>
          </div>
        </div>
      </div>

      {geoMsg && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-lg px-4 py-2">{geoMsg}</div>
      )}

      <div className="card p-0 overflow-hidden">
        <MapContainer center={mapCenter} zoom={13} className="h-[620px] w-full" scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {propertiesWithCoordinates.map((property) => {
            const lat = toNumber(property.latitude)!;
            const lng = toNumber(property.longitude)!;
            const status = getStatusMeta(property);
            const ownerNames =
              property.owners?.map((owner) => owner.person?.name).filter(Boolean).join(', ') || 'Nao informado';

            return (
              <Marker key={property.id} position={[lat, lng]} icon={buildMarkerIcon(status.color)}>
                <Popup minWidth={260}>
                  <div className="space-y-2">
                    <div
                      className="text-white text-xs font-semibold px-2 py-1 rounded inline-block"
                      style={{ backgroundColor: status.color }}
                    >
                      {status.label}
                    </div>

                    <div>
                      <p className="font-bold text-sm">{property.title}</p>
                      <p className="text-xs text-gray-500">{property.code}</p>
                    </div>

                    <div className="text-xs text-gray-700">
                      <p>
                        <strong>Proprietario:</strong> {ownerNames}
                      </p>
                      <p>
                        <strong>Endereco:</strong> {buildAddressLabel(property) || 'Nao informado'}
                      </p>
                      <p>
                        <strong>Aluguel:</strong> {formatCurrency(property.rentPrice)}
                      </p>
                      <p>
                        <strong>Venda:</strong> {formatCurrency(property.salePrice)}
                      </p>

                      {status.activeLease && (
                        <>
                          <p>
                            <strong>Inquilino:</strong> {status.activeLease.tenant?.name || 'Nao informado'}
                          </p>
                          <p>
                            <strong>Fim contrato:</strong>{' '}
                            {status.activeLease.endDate
                              ? format(parseISO(status.activeLease.endDate), 'dd/MM/yyyy', { locale: ptBR })
                              : 'Nao informado'}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      <div className="card">
        <h3 className="font-semibold mb-4">Legenda de Cores</h3>
        <div className="flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full bg-red-500 inline-block" />
            <span>Alugado</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full bg-yellow-500 inline-block" />
            <span>Manutencao/Reforma</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full bg-green-500 inline-block" />
            <span>Disponivel</span>
          </div>
        </div>
      </div>

      {propertiesWithCoordinates.length < filteredProperties.length && (
        <div className="card border border-amber-200 bg-amber-50">
          <p className="text-sm text-amber-800">
            {filteredProperties.length - propertiesWithCoordinates.length} imovel(is) nao apareceram no mapa por falta
            de coordenadas validas.
          </p>
        </div>
      )}

      {filteredProperties.length === 0 && (
        <div className="text-center py-12">
          <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Nenhum imovel encontrado para este filtro.</p>
        </div>
      )}
    </div>
  );
}
